/**
 * WEB-3310 юнит 8 — тема antd кабинета для экранов, которые пока остаются на
 * antd, внутри нового каркаса (`router/DashboardLayout.tsx` ставит её вложенным
 * `ConfigProvider` вокруг столбца страницы `main`).
 *
 * Эталон — готовый файл кита фронтендера спек-репо
 * `design/cabinet-rescin/кит-фронтендера/01-токены-и-темы.md` §15 (строки
 * 397–538 на 4974261f): те же общие токены, те же компоненты и значения.
 * Цвета — числами, а не `var(--…)`: antd считает производные цвета (наведение,
 * нажатие, подложки) из базовых, строка `var(…)` эти расчёты ломает. Смеси
 * «`--fg` N %» пересчитаны по `--fg` темы.
 *
 * Чего токенами antd не выразить (градиенты, облачко, мягкий край, шторки
 * телефона, свечение значков — кит 01:542–559) здесь нет: это даёт свой кит,
 * для экранов на antd отдельной правки CSS не делается (решение оркестратора
 * 24.10). Вход, публичные страницы и админ-панель остаются на `buildAppTheme`
 * (`theme/antdTheme.ts`) — тема ставится только внутри каркаса.
 */
import { theme as antdTheme, type ThemeConfig } from 'antd';
import type { MappingAlgorithm } from 'antd/es/theme/interface';

export type CabinetMode = 'light' | 'dark';

const fg = { dark: '230,233,240', light: '14,16,22' } as const;
/** color-mix(in srgb, var(--fg) a%, transparent) */
const mix = (m: CabinetMode, a: number) => `rgba(${fg[m]},${a})`;

const C = {
  dark: {
    page: '#151925',
    panel: '#1a1f2d',
    card: '#1c2130',
    raised: '#242a3a',
    popBg: '#1e2331',
    fg: '#e6e9f0',
    fg2: 'rgba(230,233,240,.66)',
    fg3: 'rgba(230,233,240,.52)',
    fg4: 'rgba(230,233,240,.28)',
    line: 'rgba(255,255,255,.075)',
    line2: 'rgba(255,255,255,.13)',
    hover: 'rgba(237,237,239,.06)',
    press: 'rgba(237,237,239,.1)',
    link: '#4094f7',
    accentSoft: 'rgba(42,101,242,.14)',
    ok: '#3fb68b',
    warn: '#e8b85c',
    err: '#f07171',
    shadow: '0 24px 48px -24px rgba(0,0,0,.8)',
    cardSh: '0 30px 60px -40px rgba(0,0,0,.75)',
    mask: 'rgba(4,6,12,.62)',
  },
  light: {
    page: '#f5f6f8',
    panel: '#fcfcfd',
    card: '#ffffff',
    raised: '#ffffff',
    popBg: '#ffffff',
    fg: '#0e1016',
    fg2: 'rgba(14,16,22,.78)',
    fg3: 'rgba(14,16,22,.7)',
    fg4: 'rgba(14,16,22,.36)',
    line: 'rgba(14,16,22,.13)',
    line2: 'rgba(14,16,22,.24)',
    hover: 'rgba(14,16,22,.06)',
    press: 'rgba(14,16,22,.09)',
    link: '#1f56d8',
    accentSoft: 'rgba(42,101,242,.1)',
    ok: '#15803d',
    warn: '#a16207',
    err: '#d03b3b',
    shadow:
      '0 1px 2px rgba(16,24,40,.04), 0 16px 40px -16px rgba(16,24,40,.16)',
    cardSh:
      '0 1px 2px rgba(16,24,40,.04), 0 30px 60px -40px rgba(16,24,40,.28)',
    mask: 'rgba(4,6,12,.62)',
  },
} as const;

const ACCENT = '#2a65f2';
const SANS =
  "'Nunito Sans Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Вторая функция алгоритма: возвращает цвета-«семена» кита поверх того, что
 * насчитал алгоритм темы. Тёмный алгоритм antd перекрашивает заданный цвет-семя
 * (`#2a65f2` → `#2759d1`, `#4094f7` → `#3981d5`, статусы — так же; находка
 * WEB-3277, тот же приём у пробы `buildLandingProbeTheme`), а кит требует итоговые
 * числа раздела 2. Остальные цвета кита — не семена, их `token` и так ставит
 * поверх алгоритма. Одна функция на режим — постоянная ссылка, тема от вызова к
 * вызову одинакова (antd кэширует расчёт токенов по ней).
 */
function makePinSeeds(mode: CabinetMode): MappingAlgorithm {
  const c = C[mode];
  return (seed, map) => ({
    ...(map ?? antdTheme.defaultAlgorithm(seed)),
    colorPrimary: ACCENT,
    colorInfo: ACCENT,
    colorSuccess: c.ok,
    colorWarning: c.warn,
    colorError: c.err,
    colorLink: c.link,
  });
}

const PIN_SEEDS: Record<CabinetMode, MappingAlgorithm> = {
  dark: makePinSeeds('dark'),
  light: makePinSeeds('light'),
};

/** Тема antd кабинета (кит 01 §15) для светлой или тёмной темы пользователя. */
export function buildCabinetTheme(mode: CabinetMode): ThemeConfig {
  const c = C[mode];
  return {
    algorithm: [
      mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      PIN_SEEDS[mode],
    ],
    hashed: true,
    token: {
      // акцент и статусы
      colorPrimary: ACCENT,
      colorPrimaryHover: '#2f71ff',
      colorPrimaryActive: '#275ddf',
      colorInfo: ACCENT,
      colorSuccess: c.ok,
      colorWarning: c.warn,
      colorError: c.err,
      colorLink: c.link,
      colorLinkHover: c.link,
      colorLinkActive: c.link,
      // поверхности
      colorBgLayout: c.page,
      colorBgContainer: c.card,
      colorBgElevated: c.raised,
      colorBgSpotlight: c.raised,
      colorBgMask: c.mask,
      // текст
      colorText: c.fg,
      colorTextHeading: c.fg,
      colorTextSecondary: c.fg2,
      colorTextTertiary: c.fg3,
      colorTextQuaternary: c.fg4,
      colorTextPlaceholder: c.fg4,
      colorTextDescription: c.fg3,
      colorIcon: c.fg2,
      colorIconHover: c.fg,
      // линии и заливки
      colorBorder: c.line2,
      colorBorderSecondary: c.line,
      colorSplit: c.line,
      colorFill: mix(mode, 0.11),
      colorFillSecondary: mix(mode, 0.07),
      colorFillTertiary: c.hover,
      colorFillQuaternary: mix(mode, 0.05),
      controlItemBgHover: c.hover,
      controlItemBgActive: 'rgba(42,101,242,.12)',
      controlItemBgActiveHover: 'rgba(42,101,242,.12)',
      // у полей нет линии фокуса — только каретка
      controlOutline: 'transparent',
      controlOutlineWidth: 0,
      lineWidthFocus: 1,
      // шрифт (плотный вид)
      fontFamily: SANS,
      fontSize: 13,
      fontSizeSM: 12,
      fontSizeLG: 15,
      fontSizeHeading1: 20,
      fontSizeHeading2: 15,
      fontSizeHeading3: 15,
      fontSizeHeading4: 13,
      fontSizeHeading5: 13,
      lineHeight: 1.55,
      fontWeightStrong: 700,
      // размеры и радиусы; LG — только крупная кнопка .btn.lg
      controlHeight: 32,
      controlHeightSM: 28,
      controlHeightLG: 52,
      borderRadiusXS: 4,
      borderRadiusSM: 8,
      borderRadius: 10,
      borderRadiusLG: 12,
      borderRadiusOuter: 4,
      // шкала 4·8·12·16·24·32
      sizeUnit: 4,
      sizeStep: 4,
      // тени
      boxShadow: c.shadow,
      boxShadowSecondary: c.shadow,
      boxShadowTertiary: 'none',
      // движение
      motionDurationFast: '0.16s',
      motionDurationMid: '0.22s',
      motionDurationSlow: '0.42s',
      motionEaseOut: 'cubic-bezier(.22, .8, .24, 1)',
      motionEaseInOut: 'cubic-bezier(.3, .7, .3, 1)',
      wireframe: false,
      zIndexPopupBase: 1000,
    },
    components: {
      Button: {
        borderRadius: 999,
        borderRadiusSM: 999,
        borderRadiusLG: 999,
        // по факту макета (решение владельца 24.10, README кита п. 7); крупная — 600 через CSS
        fontWeight: 400,
        contentFontSize: 13,
        contentFontSizeSM: 13,
        contentFontSizeLG: 15,
        paddingInline: 12,
        paddingInlineSM: 12,
        paddingInlineLG: 30,
        primaryShadow:
          'inset 0 1px 0 rgba(255,255,255,.26), 0 4px 12px -6px rgba(42,101,242,.7), 0 1px 2px rgba(0,0,0,.2)',
        defaultBg: mix(mode, 0.07),
        defaultColor: c.fg,
        defaultBorderColor: 'transparent',
        defaultHoverBg: mix(mode, 0.11),
        defaultHoverColor: c.fg,
        defaultHoverBorderColor: 'transparent',
        defaultActiveBg: mix(mode, 0.11),
        defaultActiveColor: c.fg,
        defaultActiveBorderColor: 'transparent',
        defaultShadow:
          'inset 0 1px 0 rgba(255,255,255,.1), 0 4px 12px -8px rgba(0,0,0,.35)',
        textHoverBg: 'transparent',
        dangerShadow: 'none',
        onlyIconSize: 16,
      },
      Input: {
        activeBg: mix(mode, 0.05),
        hoverBg: mix(mode, 0.05),
        colorBgContainer: mix(mode, 0.05),
        colorBorder: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'transparent',
        activeShadow: 'none',
        errorActiveShadow: 'none',
        warningActiveShadow: 'none',
        paddingInline: 12,
        borderRadius: 10,
      },
      Select: {
        selectorBg: mix(mode, 0.05),
        colorBorder: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'transparent',
        activeOutlineColor: 'transparent',
        optionSelectedBg: 'rgba(42,101,242,.12)',
        optionActiveBg: c.hover,
        optionSelectedFontWeight: 400,
        optionHeight: 28,
        optionPadding: '0 8px',
        optionFontSize: 13,
        borderRadius: 10,
        borderRadiusLG: 12,
      },
      Dropdown: {
        borderRadiusLG: 12,
        paddingXXS: 8,
        controlItemBgHover: c.hover,
      },
      Switch: {
        trackHeight: 24,
        trackMinWidth: 40,
        handleSize: 18,
        trackPadding: 3,
        colorPrimary: 'rgba(42,101,242,.28)',
        colorPrimaryHover: 'rgba(42,101,242,.28)',
        colorTextQuaternary: c.hover,
        colorTextTertiary: c.press,
        handleShadow: 'none',
      },
      Checkbox: {
        controlInteractiveSize: 18,
        borderRadiusSM: 4,
        colorBorder: 'transparent',
      },
      Segmented: {
        trackBg: mix(mode, 0.07),
        trackPadding: 2,
        borderRadius: 999,
        borderRadiusSM: 999,
        itemColor: c.fg3,
        itemHoverColor: c.fg,
        itemHoverBg: mix(mode, 0.06),
        itemSelectedColor: c.fg,
        // градиент облачка — CSS своего кита
        itemSelectedBg: mix(mode, 0.12),
        controlHeight: 28,
        controlHeightSM: 28,
      },
      Modal: {
        contentBg: c.popBg,
        headerBg: 'transparent',
        footerBg: 'transparent',
        titleFontSize: 20,
        titleLineHeight: 1.3,
        titleColor: c.fg,
        borderRadiusLG: 24,
        paddingContentHorizontalLG: 32,
      },
      Drawer: { colorBgElevated: c.popBg },
      // 1010 — слой подсказки кита .tip-layer (01:517); строка 01:296 (1070) противоречит блоку кода
      Tooltip: {
        colorBgSpotlight: c.raised,
        colorTextLightSolid: c.fg,
        borderRadius: 8,
        fontSize: 12,
        zIndexPopup: 1010,
      },
      Popover: { colorBgElevated: c.raised, borderRadiusLG: 12 },
      Message: {
        contentBg: c.raised,
        contentPadding: '10px 16px',
        borderRadiusLG: 18,
        fontSize: 13,
      },
      Table: {
        headerBg: 'transparent',
        headerColor: c.fg3,
        headerSplitColor: 'transparent',
        headerBorderRadius: 0,
        borderColor: c.line,
        rowHoverBg: c.hover,
        rowSelectedBg: c.accentSoft,
        rowSelectedHoverBg: c.accentSoft,
        cellPaddingInline: 12,
        cellPaddingBlock: 10,
        cellFontSize: 13,
        headerSortActiveBg: 'transparent',
        colorBgContainer: 'transparent',
      },
      Card: {
        borderRadiusLG: 12,
        paddingLG: 16,
        colorBorderSecondary: 'transparent',
        boxShadow: c.cardSh,
        colorBgContainer: mode === 'light' ? '#fcfcfd' : c.card,
      },
      Tag: {
        defaultBg: mix(mode, 0.06),
        defaultColor: c.fg2,
        colorBorder: 'transparent',
      },
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: c.fg2,
        itemHoverColor: c.fg,
        itemHoverBg: 'transparent',
        itemSelectedBg: 'transparent',
        itemSelectedColor: c.link,
        itemActiveBg: 'transparent',
        itemHeight: 38,
        iconSize: 18,
        itemMarginInline: 10,
        itemBorderRadius: 10,
        fontSize: 15,
        groupTitleColor: c.fg3,
        groupTitleFontSize: 12,
        collapsedWidth: 56,
      },
      Layout: {
        bodyBg: c.page,
        siderBg: 'transparent',
        headerBg: 'transparent',
      },
    },
  };
}
